import { a as focusRings, c as getFirstFocusable, d as colors, i as focusOutlines, l as getFocusableElements, n as focusColors, o as focusVisibleStyles, r as focusIndicatorStyles, s as focusableSelectors, t as componentFocus, u as getLastFocusable } from "./focus-DaIphPOd.mjs";
import { _ as lineHeights, a as getTransition, c as transitions, d as spacing, f as zIndices, g as letterSpacings, h as fonts, i as getAnimation, l as radii, m as fontWeights, n as durations, o as keyframes, p as fontSizes, r as easings, s as reducedMotionQuery, t as animations, u as shadows, v as typography } from "./motion-Cg-JnSSP.mjs";
import { breakpoints, cssVariables, mediaQuery, rawTokens, tailwindTheme, tokens } from "./tokens/index.mjs";
import { a as IconButton, i as Textarea, n as Badge, o as Button, r as Select, t as Switch } from "./Switch-I-T1TqTa.mjs";
import { n as Kbd, r as Input, t as StatusDot } from "./StatusDot-CP0x3IGI.mjs";
import { Keys, cn, createVariants, eStyles, generateId, isKey, noiseTexture, withOpacity } from "./utils/index.mjs";
import { a as useEscapeKey, c as useKeyboardNavigation, d as useScrollLock, f as useHaptic, g as useCanvasViewport, h as useCollaboration, i as useDebounce, l as useMediaQuery, m as useDocSync, n as useClickOutside, o as useFocusTrap, p as useVisualViewport, r as useControllableState, s as useId, t as useAutoScroll, u as usePrefersReducedMotion } from "./hooks-ChkgkdFK.mjs";
import { a as Separator, i as Skeleton, n as SearchInput, o as Checkbox, r as PathInput, t as Slider } from "./atoms-BBGUnVZq.mjs";
import { n as Label, r as Avatar, t as ThemeSwatch } from "./ThemeSwatch-JAmwSMrS.mjs";
import { t as Spinner } from "./Spinner-07Flxo1-.mjs";
import { _ as Breadcrumbs, a as SessionTabBar, c as MessageReactions, d as MemberStack, f as EmptyState, g as Card, h as NavItem, i as MobileKeyboardBar, l as ReactionPicker, m as QuickAction, n as DrawerBackdrop, o as SplitPanel, p as ProgressBar, r as PolicyConfirmDialog, s as ReadReceiptIndicator, t as BitmaskGrid, u as NotificationBell } from "./molecules-gQNF-eWR.mjs";
import { a as StatDisplay, i as Modal, n as ThemeSwitcher, o as FormField, r as TreeView, s as Tabs, t as BottomSheet } from "./BottomSheet-Cdx2XRCw.mjs";
import { a as THEMES_BY_ID, i as THEMES, n as useTheme, r as DEFAULT_THEME_ID, t as ThemeProvider } from "./context-BiO95ZTa.mjs";
import { A as useToast, C as MemoryViewer, D as BentoGrid, E as AgentPanel, M as Sidebar, O as BentoLayouts, S as ThinkingStateViewer, T as AgentOrchestra, _ as ActionConfirmation, a as GitSidebarPanel, b as ToolCallVisualizer, c as LibraryBrowser, d as SettingsFormBuilder, f as TreeLayerPanel, g as Canvas, h as CanvasNode, i as NotificationSetup, j as Header, k as ToastProvider, l as Dashboard, m as CanvasEdge, n as ActivationHeatmap, o as CommandPalette, p as PerformanceMonitor, r as SshProfilePanel, s as ExtendedThinkingViewer, t as VortexInspector, u as ThemeProfile, v as ThreadPanel, w as ConsciousnessStream, x as CouncilDeliberation, y as ShareDialog } from "./organisms-frQpQ00h.mjs";
import { t as GlassPanel } from "./GlassPanel-Cf6NZdLp.mjs";
import { a as Scanlines, c as BootSequence, i as ParticleField, l as GlowOrbs, n as Breath, o as HexGrid, r as FlowingBorder, s as TypewriterText, u as NoiseTexture } from "./effects-CVFl-MPk.mjs";
import "./themes/index.mjs";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/biomes/presets.ts
const BIOME_VOID = {
	id: "void",
	name: "Void",
	description: "Berghain 5am. Industrial silence. Minimal. Focus.",
	colors: {
		primary: "#39ff9f",
		secondary: "#1a3a2a",
		accent: "#ffffff",
		glow: "rgba(57, 255, 159, 0.3)",
		void: "#0a0a0b",
		text: "#ffffff"
	},
	motion: {
		breathCycle: 5.5,
		breathDepth: .3,
		driftSpeed: .02,
		turbulence: .1,
		waveAmplitude: 0,
		waveFrequency: 0
	},
	particles: {
		count: 12,
		mode: "firefly",
		minSize: 1,
		maxSize: 2,
		glowRadius: 4,
		blinkRate: 3,
		pauseChance: .7,
		connections: false,
		connectionDistance: 0
	},
	atmosphere: {
		fogDensity: .1,
		bloomIntensity: .3,
		noiseScale: .5,
		noiseSpeed: .01
	},
	audio: null
};
const BIOME_DEEP = {
	id: "deep",
	name: "Deep",
	description: "Bioluminescence. Ocean depths. Pressure. Deep thoughts.",
	colors: {
		primary: "#00d4ff",
		secondary: "#0066ff",
		accent: "#ff00ff",
		glow: "rgba(0, 212, 255, 0.4)",
		void: "#020810",
		text: "#88ddff"
	},
	motion: {
		breathCycle: 7,
		breathDepth: .5,
		driftSpeed: .015,
		turbulence: .2,
		waveAmplitude: .3,
		waveFrequency: .1
	},
	particles: {
		count: 25,
		mode: "bubbles",
		minSize: 2,
		maxSize: 6,
		glowRadius: 8,
		blinkRate: 8,
		pauseChance: .4,
		connections: false,
		connectionDistance: 0
	},
	atmosphere: {
		fogDensity: .4,
		bloomIntensity: .5,
		noiseScale: .8,
		noiseSpeed: .02
	},
	audio: null
};
const BIOME_NEBULA = {
	id: "nebula",
	name: "Nebula",
	description: "Stardust. Dreams. Cosmic loneliness.",
	colors: {
		primary: "#bf5fff",
		secondary: "#ff5f9f",
		accent: "#5fffff",
		glow: "rgba(191, 95, 255, 0.4)",
		void: "#0a0512",
		text: "#e0c0ff"
	},
	motion: {
		breathCycle: 8,
		breathDepth: .6,
		driftSpeed: .008,
		turbulence: .15,
		waveAmplitude: .2,
		waveFrequency: .05
	},
	particles: {
		count: 40,
		mode: "dust",
		minSize: 1,
		maxSize: 3,
		glowRadius: 6,
		blinkRate: 15,
		pauseChance: .3,
		connections: true,
		connectionDistance: 80
	},
	atmosphere: {
		fogDensity: .6,
		bloomIntensity: .7,
		noiseScale: 1.2,
		noiseSpeed: .005
	},
	audio: null
};
const BIOME_AURORA = {
	id: "aurora",
	name: "Aurora",
	description: "Northern lights. Excitement. Electrically alive.",
	colors: {
		primary: "#00ff88",
		secondary: "#00aaff",
		accent: "#ffffff",
		glow: "rgba(0, 255, 136, 0.5)",
		void: "#050a08",
		text: "#aaffcc"
	},
	motion: {
		breathCycle: 4,
		breathDepth: .7,
		driftSpeed: .04,
		turbulence: .4,
		waveAmplitude: .5,
		waveFrequency: .2
	},
	particles: {
		count: 30,
		mode: "firefly",
		minSize: 1,
		maxSize: 4,
		glowRadius: 10,
		blinkRate: 20,
		pauseChance: .2,
		connections: false,
		connectionDistance: 0
	},
	atmosphere: {
		fogDensity: .2,
		bloomIntensity: .8,
		noiseScale: .6,
		noiseSpeed: .04
	},
	audio: null
};
const BIOMES = {
	void: BIOME_VOID,
	deep: BIOME_DEEP,
	nebula: BIOME_NEBULA,
	aurora: BIOME_AURORA
};
const DEFAULT_BIOME = "void";
//#endregion
//#region src/biomes/context.tsx
/**
* EITELKEIT Biome Context & Hooks
* ═══════════════════════════════════════════════════════════════════
* React context for biome state management
* Provides CSS variables for 2D and uniforms for 3D
*/
const BiomeContext = createContext(null);
function biomeToCSSVars(biome) {
	return {
		"--biome-primary": biome.colors.primary,
		"--biome-secondary": biome.colors.secondary,
		"--biome-accent": biome.colors.accent,
		"--biome-glow": biome.colors.glow,
		"--biome-void": biome.colors.void,
		"--biome-text": biome.colors.text,
		"--biome-breath-cycle": `${biome.motion.breathCycle}s`,
		"--biome-breath-depth": String(biome.motion.breathDepth),
		"--biome-drift-speed": String(biome.motion.driftSpeed),
		"--biome-turbulence": String(biome.motion.turbulence)
	};
}
function BiomeProvider({ children, initialBiome, transitionDuration }) {
	const initBiome = initialBiome ?? "void";
	const transDur = transitionDuration ?? 2e3;
	const [biomeId, setBiomeId] = useState(initBiome);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const biome = BIOMES[biomeId];
	const cssVars = useMemo(() => biomeToCSSVars(biome), [biome]);
	const setBiome = useCallback((newId) => {
		if (newId === biomeId) return;
		setIsTransitioning(true);
		setBiomeId(newId);
		setTimeout(() => {
			setIsTransitioning(false);
		}, transDur);
	}, [biomeId, transDur]);
	const value = useMemo(() => ({
		biome,
		biomeId,
		setBiome,
		cssVars,
		isTransitioning
	}), [
		biome,
		biomeId,
		setBiome,
		cssVars,
		isTransitioning
	]);
	return /* @__PURE__ */ jsx(BiomeContext.Provider, {
		value,
		children: /* @__PURE__ */ jsx("div", {
			className: "biome-root",
			style: {
				...cssVars,
				backgroundColor: biome.colors.void,
				transition: `background-color ${transDur}ms ease-in-out`
			},
			children
		})
	});
}
/**
* Access the current biome context
*/
function useBiome() {
	const context = useContext(BiomeContext);
	if (!context) throw new Error("useBiome must be used within a BiomeProvider");
	return context;
}
/**
* Get a specific biome by ID (doesn't require context)
*/
function getBiome(id) {
	return BIOMES[id];
}
function selectBiomeFromState(state) {
	if (state.engagement < .3) return "void";
	if (state.resonance > .8) return "deep";
	if (state.effort > .7) return "nebula";
	if (state.engagement > .8) return "aurora";
	return "void";
}
//#endregion
export { ActionConfirmation, ActivationHeatmap, AgentOrchestra, AgentPanel, Avatar, BIOMES, BIOME_AURORA, BIOME_DEEP, BIOME_NEBULA, BIOME_VOID, Badge, BentoGrid, BentoLayouts, BiomeProvider, BitmaskGrid, BootSequence, BottomSheet, Breadcrumbs, Breath, Button, Canvas, CanvasEdge, CanvasNode, Card, Checkbox, CommandPalette, ConsciousnessStream, CouncilDeliberation, DEFAULT_BIOME, DEFAULT_THEME_ID, Dashboard, DrawerBackdrop, EmptyState, ExtendedThinkingViewer, FlowingBorder, FormField, GitSidebarPanel, GlassPanel, GlowOrbs, Header, HexGrid, IconButton, Input, Kbd, Keys, Label, LibraryBrowser, MemberStack, MemoryViewer, MessageReactions, MobileKeyboardBar, Modal, NavItem, NoiseTexture, NotificationBell, NotificationSetup, ParticleField, PathInput, PerformanceMonitor, PolicyConfirmDialog, ProgressBar, QuickAction, ReactionPicker, ReadReceiptIndicator, Scanlines, SearchInput, Select, Separator, SessionTabBar, SettingsFormBuilder, ShareDialog, Sidebar, Skeleton, Slider, Spinner, SplitPanel, SshProfilePanel, StatDisplay, StatusDot, Switch, THEMES, THEMES_BY_ID, Tabs, Textarea, ThemeProfile, ThemeProvider, ThemeSwatch, ThemeSwitcher, ThinkingStateViewer, ThreadPanel, ToastProvider, ToolCallVisualizer, TreeLayerPanel, TreeView, TypewriterText, VortexInspector, animations, breakpoints, cn, colors, componentFocus, createVariants, cssVariables, durations, eStyles, easings, focusColors, focusIndicatorStyles, focusOutlines, focusRings, focusVisibleStyles, focusableSelectors, fontSizes, fontWeights, fonts, generateId, getAnimation, getBiome, getFirstFocusable, getFocusableElements, getLastFocusable, getTransition, isKey, keyframes, letterSpacings, lineHeights, mediaQuery, noiseTexture, radii, rawTokens, reducedMotionQuery, selectBiomeFromState, shadows, spacing, tailwindTheme, tokens, transitions, typography, useAutoScroll, useBiome, useCanvasViewport, useClickOutside, useCollaboration, useControllableState, useDebounce, useDocSync, useEscapeKey, useFocusTrap, useHaptic, useId, useKeyboardNavigation, useMediaQuery, usePrefersReducedMotion, useScrollLock, useTheme, useToast, useVisualViewport, withOpacity, zIndices };

//# sourceMappingURL=index.mjs.map